// What every detector reads: one engine line from one position, seen from
// the side that gets the tactic. For a blunder that is the opponent's best
// reply from the position after the move; for a miss, the reader's best line
// from the position before it (`lineOf`). A detector returns the motif it
// finds in the line's first move (and, for a few, the plies after it), or
// nothing.
import type { Chess } from '@chess/api-chess';
import { uciToInput } from '@chess/api-chess';
import type { Color, Fen, LostPiece, Uci } from '@chess/api-model';
import type { Motif, MotifInput } from '@chess/api-mistakes';
import { Board, opposite, sideToMove } from './rays.ts';

export interface TacticLine {
  /** The position the line starts from, `side` to move. */
  fen: Fen;
  board: Board;
  /** The side that gets the tactic: the opponent after a blunder, the reader before a miss. */
  side: Color;
  victim: Color;
  /** The engine's line from `fen`, `side`'s move first. */
  pv: Uci[];
  /** Moves to mate for `side`, when the line is a forced mate. */
  mate?: number;
  /** The net material the line wins (the fact's `lost` or `missed`), when it wins any. */
  material?: LostPiece;
  /** The rules, for legality: a trapped piece's escapes, a mate at the end of the line. */
  chess: Chess;
}

export type Detector = (line: TacticLine) => Motif | undefined;

/** The line a mistake is classified on, or undefined when the fact carries none. */
export function lineOf(input: MotifInput, chess: Chess): TacticLine | undefined {
  const blunder = input.kind === 'blunder';
  const fen = blunder ? input.fenAfter : input.fenBefore;
  const engine = blunder ? input.reply : input.best;
  const side = blunder ? opposite(input.reader) : input.reader;
  if (!fen || sideToMove(fen) !== side) return undefined;
  return {
    fen,
    board: Board.fromFen(fen),
    side,
    victim: opposite(side),
    pv: engine?.pv ?? [],
    mate: engine?.mate,
    material: blunder ? input.lost : input.missed,
    chess,
  };
}

/** The position after the line's first `plies` moves, by the rules; undefined when a move is not legal. */
export function positionAfter(line: TacticLine, plies: number): Fen | undefined {
  let fen = line.fen;
  for (const uci of line.pv.slice(0, plies)) {
    const played = line.chess.play(fen, uciToInput(uci));
    if (!played) return undefined;
    fen = played.fen;
  }
  return fen;
}
